import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { AuthHandler } from '../auth/AuthHandler';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { MaybeWithLinks } from '../caremap/WithLinks';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { isExpectedFetchError } from '../support/isExpectedFetchError';
import { Logger } from '../support/Logger';

export interface IConsentRecord {
    consentType: string;
    consentedAt: string;
    disclosureVersion: string;
    disclosureText: string;
    phone?: string;
}

export interface IUser {
    userId?: string;
    firstName?: string;
    lastName?: string;
    pictureUrl?: string;
    avatar?: string;
    color?: string;
    email?: string;
    phone?: string;
    workPhone?: string;
    address?: string;
    consents?: IConsentRecord[];
    updatedTime?: string;
    createdTime?: string; // note older users are missing this
};

export interface ILightUser {
    userId: string;
    firstName: string;
    lastName: string;
    pictureUrl: string;
};

/**
 * UserState is loaded in AppContainer before the app starts, and therefore will always be loaded.
 */
export class UserState extends State<MaybeWithLinks<IUser>> {

    isDirty = false;

    static inject = () => [
        APIClient,
        AuthHandler,
        BufferedEventBus,
        Logger
    ];

    constructor(
        private apiClient: APIClient,
        private authHandler: AuthHandler,
        private events: BufferedEventBus,
        private logger: Logger
    ) {
        super({});

        this.events.on('web-sockets.message', this.onWebsocketMessage);
    }

    private onWebsocketMessage = async (message?: IResourceChangedSocketMessage) => {
        const selfHref = this.state.links?.self?.href;
        if (!selfHref || !message || message.resourceToRefresh.toLowerCase() !== selfHref.toLowerCase()) {
            return;
        }

        await this.reload();
    };

    load = async () => {
        const entry = await this.apiClient.entry();
        if (!entry.links.user){
            return;
        }
        const user = await this.apiClient.get(entry.links.user);

        if (user) {
            this.setState(user);
        }
    };

    reload = async () => {
        try {
            await this.load();
        } catch (err: any) {
            // a transient/aborted reload is expected — it retries on the next trigger.
            // Alert only on genuine failures.
            if (!isExpectedFetchError(err)) {
                this.logger.error(`UserState.reload failed: ${err.toString()}`);
            }
        }
    };

    getName = (): string => {
        if (this.state.firstName && this.state.lastName){
            return `${this.state.firstName} ${this.state.lastName}`;
        }
        return this.state.firstName || '';
    };

    // A REAL account save: PUTs /user and emits 'UserState.identity-changed' so linked member nodes
    // update optimistically. Call this for genuine account edits (Account Settings, onboarding).
    // Do NOT call it from the linked-member → account optimistic sync-back
    // (CareMapState.applyLinkedMemberIdentityBackToAccount): that path only mirrors an edit the
    // caremap PUT already persisted server-side, so it updates local state without a PUT — see the
    // long note there.
    save = async () => {
        const entry = await this.apiClient.entry();
        if (!entry.links.user){
            throw new Error('not supported');
        }

        await this.apiClient.put(entry.links.user, this.state);
        this.isDirty = false;
        this.setState({ });

        // Emit with the saved identity so CareMapState can apply it optimistically to all linked
        // member nodes across every loaded caremap — avoiding CareMapState → UserState circular
        // injection by using the event bus instead of a direct call.
        if (this.state.userId) {
            this.events.emit('UserState.identity-changed', { userId: this.state.userId, user: this.state });
        }

        this.events.emit('UserState.saved');
    };

    onSaved = (listener: () => void) => {
        return this.events.on('UserState.saved', listener);
    };

    deleteAccount = async () => {
        const entry = await this.apiClient.entry();
        if (!entry.links.user){
            throw new Error('not supported');
        }

        this.events.emit('UserState.deleted');

        await this.apiClient.post(entry.links.deleteAccount, undefined);

        await this.authHandler.logout();
    };

    setFirstName = (firstName: string) => {
        this.isDirty = true;
        this.setState({ firstName });
    };

    setLastName = (lastName: string) => {
        this.isDirty = true;
        this.setState({ lastName });
    };

    setPhone = (phone: string) => {
        this.isDirty = true;
        this.setState({ phone });
    };

    setWorkPhone = (workPhone: string) => {
        this.isDirty = true;
        this.setState({ workPhone });
    };

    setAddress = (address: string) => {
        this.isDirty = true;
        this.setState({ address });
    };

    setPictureUrl = (pictureUrl: string) => {
        this.isDirty = true;
        this.setState({ pictureUrl });
    };

    setAvatar = (avatar: string) => {
        this.isDirty = true;
        this.setState({ avatar });
    };

    setColor = (color: string) => {
        this.isDirty = true;
        this.setState({ color });
    };

    addConsent = (consent: IConsentRecord) => {
        this.isDirty = true;
        const existing = this.state.consents || [];
        this.setState({ consents: [...existing, consent] });
    };

    hasCompleteProfile = () => {
        return Boolean(this.getName() && this.state.address && this.state.phone);
    };

    hasSavedName = () => {
        return Boolean(this.state.firstName && !this.isDirty);
    };

}
